import { pipe } from 'fp-ts/function';
import { array, ord, string } from 'fp-ts';
import { useCallback } from 'react';
import { omitFalsyValues } from '@mews/fp';
import { lt, showSuccessAlert } from '@mews/framework';
import { upperFirst } from '@mews/std';
import { DropdownItem, DropdownItemProps } from '@optimus-web/core';
import { DetailedUser, EnterpriseProfilePrivileges, OverrideComparator } from '@mews-commander/data-access';
import { EnterpriseProfilePrivilegesEnumNames } from '@mews-commander/legacy-data-structures';
import { copyToClipboard } from '@mews-commander/utils';
import { ApplicationState } from '@mews-commander/types';
import {
    canCurrentProfileManageEnterpriseEmployees,
    getCurrentEnterpriseSubscriptionId,
} from '@mews-commander/legacy-entities';
import { isRoleManagementEnabled } from '@mews-commander/legacy-entities/src/ducks/subscriptions';
import { useLocalizeOption } from '@mews-commander/form';
import { RolesAndPermissionsOption } from './types';

export type FormOption = Pick<DropdownItemProps, 'value' | 'label'> & { key: string };

export const createListItems = (options: FormOption[]) => options.map((option) => <DropdownItem {...option} />);

export const getRolesAndPermissionsOption = (userProfile: DetailedUser) => {
    if (userProfile.isAdmin) {
        return RolesAndPermissionsOption.Admin;
    } else if (userProfile.roles?.length) {
        return RolesAndPermissionsOption.Roles;
    } else if (userProfile.privileges && getPrivilegesArray(userProfile.privileges).length) {
        return RolesAndPermissionsOption.CustomizedPermissions;
    }

    return RolesAndPermissionsOption.NoPermissions;
};

export const getPrivilegesArray = (data: Partial<Record<keyof EnterpriseProfilePrivileges, boolean>>) =>
    pipe(
        data,
        omitFalsyValues,
        Object.keys,
        array.map((result: string) => upperFirst(result) as EnterpriseProfilePrivilegesEnumNames)
    );

export const hasDifferentRolesAndPermissions = (fieldsConfig: (keyof OverrideComparator)[]) => {
    const fieldsToCheck = ['equalRoles', 'equalPrivileges', 'equalAdmins', 'equalOptions'];
    return fieldsConfig.some((val) => fieldsToCheck.includes(val));
};

export const orderByLabel = pipe(
    string.Ord,
    ord.contramap(({ label }: { label: string }) => label.toLocaleLowerCase())
);

export const handleCopyToClipboard = (id?: string | null) => {
    if (!id) {
        return null;
    }

    const success = copyToClipboard(id);

    if (success) {
        showSuccessAlert({ description: lt('CopiedSuccessMessage') });
    }
};

export const canManageRoles = (state: ApplicationState) => {
    const subscriptionId = getCurrentEnterpriseSubscriptionId(state);
    return canCurrentProfileManageEnterpriseEmployees(state) && isRoleManagementEnabled(state, { subscriptionId });
};

const PRIVILEGE_KEY_PREFIX = 'EnterpriseProfilePrivileges';

// this hook is duplicated in roles module. It's not possible to import without circular dependency
// It should be possible to delete when the user management is migrated to its own package
export const useLocalizePrivilege = () => {
    const localizeOption = useLocalizeOption();
    return useCallback(
        (privilege: EnterpriseProfilePrivilegesEnumNames) => localizeOption(PRIVILEGE_KEY_PREFIX, privilege),
        [localizeOption]
    );
};
